/**
 * Support Tab — ticket-based messaging system.
 * Shows RN's ticket conversations with ability to create new chats.
 *
 * The list is paged (GET /rn/tickets/feed): 20 chats at first, 20 more each
 * time she reaches the bottom. Some nurses have thousands of tickets, and the
 * old screen downloaded all of them on every focus and every new message.
 * Chats with unread messages come first, so the tab badge always points at
 * something on the first screen.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useRef, useState } from "react";
import {
    ActivityIndicator,
    FlatList,
    RefreshControl,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { TicketCard } from "@/components/tickets/TicketList";
import { colors, fonts, gradients, shadows, spacing } from "@/lib/theme";
import { fetchRnTicketFeed, type Ticket } from "@/lib/ticket-api";
import { useWebSocketEvent, useWebSocketReconnect } from "@/lib/websocket";

const PAGE_SIZE = 20;
/** A refresh reloads what is already on screen in one request, up to this many. */
const MAX_RELOAD = 100;

export default function SupportScreen() {
    const router = useRouter();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [pulling, setPulling] = useState(false);

    const ticketsRef = useRef(tickets);
    ticketsRef.current = tickets;
    // Bumped by every reload, so a "load more" that started before it is dropped
    // instead of appending rows from the old list.
    const generation = useRef(0);
    const reloadInFlight = useRef(false);
    const reloadQueued = useRef(false);
    const loadMoreInFlight = useRef(false);

    // Reload from the top, as many rows as are already on screen, so new
    // messages and read chats land in the right place without losing her
    // scroll. Single-flight: a burst of websocket events while one reload is
    // running collapses into one follow-up reload.
    const reload = useCallback(async (limit?: number) => {
        if (reloadInFlight.current) {
            reloadQueued.current = true;
            return;
        }
        reloadInFlight.current = true;
        try {
            do {
                reloadQueued.current = false;
                const size =
                    limit ?? Math.min(Math.max(ticketsRef.current.length, PAGE_SIZE), MAX_RELOAD);
                try {
                    const page = await fetchRnTicketFeed({ limit: size });
                    generation.current += 1;
                    setTickets(page.data);
                    setNextCursor(page.nextCursor);
                } catch {
                    // Keep what is on screen; the next focus or event retries.
                }
            } while (reloadQueued.current);
        } finally {
            reloadInFlight.current = false;
            setLoading(false);
        }
    }, []);

    const loadMore = useCallback(async () => {
        if (!nextCursor || loadMoreInFlight.current || reloadInFlight.current) {
            return;
        }
        loadMoreInFlight.current = true;
        setLoadingMore(true);
        const startedAt = generation.current;
        try {
            const page = await fetchRnTicketFeed({ cursor: nextCursor, limit: PAGE_SIZE });
            if (generation.current !== startedAt) {
                return;
            }
            setTickets((prev) => {
                const seen = new Set(prev.map((t) => t.id));
                return [...prev, ...page.data.filter((t) => !seen.has(t.id))];
            });
            setNextCursor(page.nextCursor);
        } catch {
            // nextCursor is unchanged, so reaching the bottom again retries.
        } finally {
            loadMoreInFlight.current = false;
            setLoadingMore(false);
        }
    }, [nextCursor]);

    const pullToRefresh = useCallback(async () => {
        setPulling(true);
        await reload(PAGE_SIZE);
        setPulling(false);
    }, [reload]);

    useFocusEffect(
        useCallback(() => {
            void reload();
        }, [reload])
    );

    // A new message moves its chat to the top and a status change can change
    // its card, so reload on both.
    useWebSocketEvent("ticket:message", () => void reload());
    useWebSocketEvent("ticket:updated", () => void reload());
    // Catch up after a socket reconnect — covers updates missed while the socket
    // was down and the tab stayed mounted (no focus change to trigger a refetch).
    useWebSocketReconnect(() => void reload());

    const openTicket = useCallback((id: number) => router.push(`/tickets/${id}`), [router]);

    // Unread chats lead the list; label them, and label where the rest begin.
    const sectionTitleAt = (index: number): string | null => {
        const unread = tickets[index].unreadCount > 0;
        const prevUnread = index > 0 && tickets[index - 1].unreadCount > 0;
        if (index === 0) {
            return unread ? "New messages" : "Conversations";
        }
        return !unread && prevUnread ? "Conversations" : null;
    };

    const header = (
        <>
            {/* Header */}
            <View style={styles.header}>
                <View>
                    <Text style={styles.headerTitle}>Messages</Text>
                    <Text style={styles.headerSub}>Chat with your coordinator</Text>
                </View>
            </View>

            {/* New Chat Button */}
            <View style={styles.section}>
                <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => router.push("/tickets/new")}
                    style={styles.newChatOuter}
                >
                    <LinearGradient
                        colors={gradients.brand}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.newChatGradient}
                    >
                        <View style={styles.newChatContent}>
                            <MaterialCommunityIcons
                                name="chat-plus"
                                size={20}
                                color={colors.surface}
                            />
                            <Text style={styles.newChatText}>New Chat</Text>
                        </View>
                    </LinearGradient>
                </TouchableOpacity>
            </View>
        </>
    );

    return (
        <SafeAreaView style={styles.safeArea} edges={["top"]}>
            <LinearGradient
                colors={["rgba(69,210,176,0.08)", "rgba(231,72,245,0.03)", "rgba(255,255,255,0)"]}
                locations={[0, 0.12, 0.28]}
                style={styles.backgroundGradient}
            />

            <FlatList
                style={styles.list}
                contentContainerStyle={styles.listContent}
                showsVerticalScrollIndicator={false}
                data={loading ? [] : tickets}
                keyExtractor={(item) => String(item.id)}
                renderItem={({ item, index }) => {
                    const title = sectionTitleAt(index);
                    return (
                        <View style={styles.row}>
                            {title && (
                                <Text
                                    style={[
                                        styles.sectionTitle,
                                        index > 0 && styles.sectionTitleAfterCards
                                    ]}
                                >
                                    {title}
                                </Text>
                            )}
                            <View style={styles.cardInset}>
                                <TicketCard ticket={item} onPress={openTicket} />
                            </View>
                        </View>
                    );
                }}
                ListHeaderComponent={header}
                ListEmptyComponent={
                    loading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator color={colors.teal400} />
                        </View>
                    ) : (
                        <View style={styles.emptyContainer}>
                            <MaterialCommunityIcons
                                name="chat-outline"
                                size={48}
                                color={colors.textTertiary}
                            />
                            <Text style={styles.emptyTitle}>No conversations yet</Text>
                            <Text style={styles.emptySubtitle}>
                                Tap "New Chat" to start a conversation
                            </Text>
                        </View>
                    )
                }
                ListFooterComponent={
                    loadingMore ? (
                        <View style={styles.footer}>
                            <ActivityIndicator color={colors.teal400} />
                        </View>
                    ) : null
                }
                onEndReached={() => void loadMore()}
                onEndReachedThreshold={0.5}
                refreshControl={
                    <RefreshControl
                        refreshing={pulling}
                        onRefresh={() => void pullToRefresh()}
                        tintColor={colors.teal400}
                    />
                }
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: colors.background },
    backgroundGradient: { ...StyleSheet.absoluteFill },
    list: { flex: 1 },
    listContent: { paddingBottom: spacing["3xl"] },

    header: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.lg,
        paddingBottom: spacing.md
    },
    headerTitle: {
        fontFamily: fonts.bold,
        fontSize: 28,
        color: colors.textPrimary
    },
    headerSub: {
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textSecondary,
        marginTop: 2
    },

    section: { paddingHorizontal: spacing.lg, marginBottom: spacing.lg },
    row: { paddingHorizontal: spacing.lg },
    // Same card inset the sectioned list had (section padding + list padding).
    cardInset: { paddingHorizontal: spacing.base },
    sectionTitle: {
        fontFamily: fonts.semiBold,
        fontSize: 15,
        color: colors.textSecondary,
        marginBottom: spacing.sm,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    sectionTitleAfterCards: { marginTop: spacing.lg },

    newChatOuter: {
        borderRadius: 16,
        overflow: "hidden",
        ...shadows.sm
    },
    newChatGradient: {
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        borderRadius: 16
    },
    newChatContent: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm
    },
    newChatText: {
        fontFamily: fonts.semiBold,
        fontSize: 16,
        color: colors.surface
    },

    loadingContainer: {
        paddingVertical: spacing["3xl"],
        alignItems: "center"
    },
    footer: {
        paddingVertical: spacing.lg,
        alignItems: "center"
    },
    emptyContainer: {
        alignItems: "center",
        paddingVertical: spacing["3xl"]
    },
    emptyTitle: {
        fontFamily: fonts.semiBold,
        fontSize: 16,
        color: colors.textPrimary,
        marginTop: spacing.base
    },
    emptySubtitle: {
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textSecondary,
        marginTop: spacing.xs
    }
});
